"use client";

import {
  CloseButton,
  IconButton,
  Input,
  InputGroup,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import useVoiceRecorder from "@/features/audio-input/hooks/useVoiceRecorder";
import useTranscribeAudioMutation from "@/features/origins/mutations/useTranscribeAudioMutation";
import type { SourcedWhere } from "@/generated/fastapi";
import { showErrorToast, showInfoToast } from "@/shared/feedback";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";

type ProfileReviewSearchInputProps = {
  ariaLabel: string;
  onLongPressResetSearchSettings?: () => void;
  onOpenSearchSettings: () => void;
  onQueryChange: (value: string) => void;
  onResetQuery: () => void;
  placeholder: string;
  query: string;
  searchSettingsHighlighted?: boolean;
  searchSettingsLabel?: string;
  searchSettingsResetEnabled?: boolean;
  voiceSearchSource?: SourcedWhere;
};

function ProfileReviewVoiceSearchAction({
  disabled = false,
  onTranscript,
  sourcedWhere,
}: {
  disabled?: boolean;
  onTranscript: (text: string) => void;
  sourcedWhere: SourcedWhere;
}) {
  const {
    error,
    isSupported,
    notice,
    recording,
    resetRecording,
    startRecording,
    status,
    stopRecording,
  } = useVoiceRecorder();
  const transcribeAudioMutation = useTranscribeAudioMutation();
  const processedRecordingRef = useRef<string | null>(null);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (notice) {
      showInfoToast(notice);
    }
  }, [notice]);

  useEffect(() => {
    if (!recording || status !== "recorded") {
      return;
    }

    if (processedRecordingRef.current === recording.objectUrl) {
      return;
    }

    processedRecordingRef.current = recording.objectUrl;

    void (async () => {
      try {
        const result = await transcribeAudioMutation.mutateAsync({
          selectedFile: recording.file,
          sourcedWhere,
        });
        const transcript = result.transcribed_text?.trim() ?? "";

        if (!transcript) {
          showErrorToast("No speech was detected in the recording.");
          return;
        }

        onTranscript(transcript);
      } catch (transcriptionError) {
        const message =
          transcriptionError instanceof Error
            ? transcriptionError.message
            : "Failed to transcribe audio";
        showErrorToast(message);
      } finally {
        resetRecording();
        processedRecordingRef.current = null;
      }
    })();
  }, [
    onTranscript,
    recording,
    resetRecording,
    sourcedWhere,
    status,
    transcribeAudioMutation,
  ]);

  const isRecording = status === "recording";
  const isTranscribing = transcribeAudioMutation.isPending;
  const icon = isTranscribing
    ? "transcribe"
    : isRecording
      ? "stop"
      : "microphone";
  const humanReadable = isTranscribing
    ? "Transcribing voice search"
    : isRecording
      ? "Stop voice search recording"
      : isSupported
        ? "Start voice search recording"
        : "Voice search unavailable";

  const handleClick = () => {
    if (isTranscribing || disabled) {
      return;
    }

    if (isRecording) {
      stopRecording();
      return;
    }

    void startRecording();
  };

  return (
    <IconButton
      aria-label={humanReadable}
      variant="ghost"
      size="xs"
      colorPalette={isRecording ? "red" : "gray"}
      color={isSupported ? undefined : "moveon.text.muted"}
      disabled={disabled || isTranscribing}
      onClick={handleClick}
    >
      <GenericIcon
        icon={icon}
        size="sm"
        humanReadable={humanReadable}
        loading={isTranscribing}
      />
    </IconButton>
  );
}

export default function ProfileReviewSearchInput({
  ariaLabel,
  onLongPressResetSearchSettings,
  onOpenSearchSettings,
  onQueryChange,
  onResetQuery,
  placeholder,
  query,
  searchSettingsHighlighted = false,
  searchSettingsLabel = "Search settings",
  searchSettingsResetEnabled = false,
  voiceSearchSource = "studio",
}: ProfileReviewSearchInputProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchShellRef = useRef<HTMLDivElement | null>(null);

  return (
    <InputGroup
      ref={searchShellRef}
      w="full"
      flex="1"
      minW={0}
      border="md"
      borderColor="moveon.primaryLight"
      borderRadius="md"
      _focusWithin={{
        borderColor: "moveon.primary",
      }}
      endElement={
        isSearchFocused ? (
          query ? (
            <CloseButton
              size="xs"
              variant="plain"
              onClick={onResetQuery}
              aria-label={`Clear ${ariaLabel.toLowerCase()}`}
            />
          ) : (
            <ProfileReviewVoiceSearchAction
              sourcedWhere={voiceSearchSource}
              onTranscript={onQueryChange}
            />
          )
        ) : onLongPressResetSearchSettings ? (
          <WithLongPress
            onLongPress={onLongPressResetSearchSettings}
            enabled={searchSettingsResetEnabled}
            preventClickAfterLongPress={true}
            longPressHint={
              searchSettingsResetEnabled
                ? "Press long to reset search settings"
                : undefined
            }
          >
            <IconButton
              variant="ghost"
              size="xs"
              onClick={onOpenSearchSettings}
              title={searchSettingsLabel}
              aria-label={searchSettingsLabel}
              colorPalette="gray"
            >
              <GenericIcon
                icon="adjust"
                size="sm"
                highlighted={searchSettingsHighlighted}
                humanReadable={searchSettingsLabel}
              />
            </IconButton>
          </WithLongPress>
        ) : (
          <IconButton
            variant="ghost"
            size="xs"
            onClick={onOpenSearchSettings}
            title={searchSettingsLabel}
            aria-label={searchSettingsLabel}
            colorPalette="gray"
          >
            <GenericIcon
              icon="adjust"
              size="sm"
              highlighted={searchSettingsHighlighted}
              humanReadable={searchSettingsLabel}
            />
          </IconButton>
        )
      }
    >
      <Input
        w="full"
        minW={0}
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        size="sm"
        fontSize={{ base: "14px", md: "sm" }}
        onFocus={() => {
          setIsSearchFocused(true);
        }}
        onBlur={(event) => {
          const nextFocused = event.relatedTarget;

          if (
            nextFocused instanceof Node &&
            searchShellRef.current?.contains(nextFocused)
          ) {
            return;
          }

          setIsSearchFocused(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onResetQuery();
          }
        }}
        style={{
          border: "none",
          borderRadius: 0,
          overscrollBehavior: "contain",
          touchAction: "manipulation",
          resize: "none",
          overflow: "hidden",
          width: "100%",
        }}
      />
    </InputGroup>
  );
}
